import Store, { actions } from '@store';
import { debounce } from 'src/utils/toolbelt';
import getActivePage from '../../helpers/active-page-details';
import { getTextMockInstance } from 'src/factories/textmock';

const addProjectFacadeListeners = ((initialized = false) => (ProjectFacade) => {
  if (initialized) return;
  initialized = true;

  ProjectFacade.once('pages.fetched', (pages) => onPagesFetched(pages));

  ProjectFacade.once('project.fetched', (data) => onProjectFetched(data));

  ProjectFacade.on('change_page_order', (newOrder) => onChangePageOrder(newOrder));

  ProjectFacade.on('design.added', (page) => onPageAdded(page));

  ProjectFacade.on('design.removed', ({ design_id: id }) => onPageDeleted(id));

  ProjectFacade.on('design.copied', ({ design_id: id, based_on: basedOn }) => onPageDuplicated(id, basedOn));

  ProjectFacade.on('refresh_page_info', () => {
    Store.dispatch(actions.editor.setPagesOrder(
      ProjectFacade.get('designs').map(page => page.id)
    ));
  });

  function onPagesFetched (pages) {
    Store.dispatch(actions.navigation.toggleLockedTextOverflow(true));

    pages.forEach(page => Store.dispatch(actions.page.setPage({
      ...page,
      id: page.id,
      thumbnail: page.thumbnail
    })));

    Store.dispatch(actions.editor.setPagesOrder(
      ProjectFacade.get('designs').map(page => page.id)
    ));

    Store.dispatch(actions.navigation.toggleLockedTextOverflow(false));
  }

  function onProjectFetched (data) {
    if (!data?.metadata) return;

    Store.dispatch(actions.metadata.setMetadata(data.metadata));
    Store.dispatch(actions.editor.setTextLinksFromMetadata(data.metadata));
  }

  function onChangePageOrder (newOrder) {
    Store.dispatch(actions.editor.setPagesOrder(
      newOrder ||
      ProjectFacade.get('designs').map(page => page.id))
    );
  }

  function onPageAdded (page) {
    const width = page.size?.current_size?.width || page.size?.width;
    const height = page.size?.current_size?.height || page.size?.height;
    const unit = page.size?.current_size ? undefined : page.size?.unit;

    Store.dispatch(actions.page.setPage({ ...page, width, height, unit }));
  }

  function onPageDeleted (id) {
    Store.dispatch(actions.page.deletePage({ id }));

    onChangePageOrder();
    INKIVE.AdvancedOrderedListFactory.calculateOrderTrackingFromPreviousPages();
    INKIVE.AdvancedOrderedListFactory.updateTextsInPage();
  }

  function onPageDuplicated (id, basedOn) {
    Store.dispatch(actions.page.duplicatePage({ id, basedOn }));

    onChangePageOrder();
    INKIVE.AdvancedOrderedListFactory.calculateOrderTrackingFromPreviousPages();
    INKIVE.AdvancedOrderedListFactory.updateTextsInPage();
    INKIVE.AnimationFactory.copyPageConfiguration(basedOn, id);
  }
})(false);

function addEditorFacadeListeners (EditorFacade) {
  const thumbnailDebounceTime = 1000;

  EditorFacade.on('page.move_to', () => onPageChange());

  EditorFacade.on('history.change', debounce(renderActivePageThumbnail, thumbnailDebounceTime));

  EditorFacade.on('history.undo', debounce(renderActivePageThumbnail, thumbnailDebounceTime));

  EditorFacade.on('history.redo', debounce(renderActivePageThumbnail, thumbnailDebounceTime));

  EditorFacade.on('page.saved.content', ({ id, content, skipRenderThumbnail }) => onPageSaved({ id, content, skipRenderThumbnail }));

  EditorFacade.on('zoom.changed', ({ zoom }) => {
    Store.dispatch(actions.editor.setZoom(zoom));
  });

  EditorFacade.on('canvas.relativeSize', (canvasDimensions) => {
    Store.dispatch(actions.editor.setCanvasRelativeSize(canvasDimensions));
  });

  EditorFacade.on('canvas.changeLayer', ({ name }) => {
    Store.dispatch(actions.editor.setCurrentLayer(name));
  });

  async function onPageChange () {
    const activePage = getActivePage();
    const state = Store.getState();
    const fetchedPages = Object.keys(state?.elements || {}).map(Number);

    const activePageId = Number(activePage.id);
    const orderedListChains = INKIVE.AdvancedOrderedListFactory.findPagesContainChain(activePageId);
    const textOverflowChains = INKIVE.TextOverflowFactory.findPagesInSameChain(activePageId);
    const pagesToFetch = new Set([
      ...Array.from(orderedListChains),
      ...textOverflowChains
    ].filter(pageId => !fetchedPages.includes(Number(pageId))));

    getTextMockInstance().emptyTextMock();
    Store.dispatch(actions.editor.setActivePage(activePage.id));

    if (pagesToFetch.size > 0) {
      Store.dispatch(actions.navigation.toggleLockedTextOverflow(true));
      await Store.dispatch(actions.page.fetchPages({ pages: [...pagesToFetch], skipRenderThumbnail: true }));
      Store.dispatch(actions.navigation.toggleLockedTextOverflow(false));
    }

    Store.dispatch(actions.page.setPage({ ...activePage, skipRenderThumbnail: true }));

    INKIVE.TextOverflowFactory.addTextElementsToTextMock(activePageId);
    EditorFacade.trigger('page.changed', activePage);
  }

  function onPageSaved ({ id, content, skipRenderThumbnail }) {
    Store.dispatch(actions.page.setPage({ id, content, skipRenderThumbnail }));
  }
}

function renderActivePageThumbnail () {
  const pagesPanelOpen = INKIVE.C.PAGES_PANEL_OPEN;
  if (!pagesPanelOpen) return;

  const activePage = getActivePage();
  Store.dispatch(actions.page.createPageThumbnail(activePage));
}

function addDesignFacadeListeners (DesignFacade) {
  DesignFacade.on('delete', (elementId) => onElementDelete(elementId));
  DesignFacade.on('template.update.template_applied_on_current_page', () => renderActivePageThumbnail());

  function onElementDelete (elementId) {
    const pageId = INKIVE.PageFactory.getCurrentPageId();

    Store.dispatch(actions.elements.deleteElement({ elementId, pageId }));
  }
}

function configureDispatchers ({
  ProjectFacade,
  EditorFacade,
  DesignFacade
}) {
  addProjectFacadeListeners(ProjectFacade);
  addEditorFacadeListeners(EditorFacade);
  addDesignFacadeListeners(DesignFacade);
}

export default {
  configureDispatchers,
  addProjectFacadeListeners
};
